/**
 * Shopify API Functions
 * High-level API functions for interacting with Shopify Storefront
 */

import { shopifyFetch, getCartId, setCartId, removeCartId } from './client';
import * as queries from './queries';
import type {
  Product,
  Collection,
  Cart,
  Shop,
  SortKey,
  ProductFilter,
  PageInfo,
} from './types';

// ============================================
// Shop
// ============================================

export async function getShopInfo(): Promise<Shop> {
  const data = await shopifyFetch<{ shop: Shop }>({
    query: queries.GET_SHOP_INFO,
  });
  return data.shop;
}

// ============================================
// Products
// ============================================

interface GetProductsParams {
  first?: number;
  after?: string;
  sortKey?: SortKey;
  reverse?: boolean;
  query?: string;
}

interface ProductsResponse {
  products: Product[];
  pageInfo: PageInfo;
}

export async function getProducts({
  first = 24,
  after,
  sortKey = 'BEST_SELLING',
  reverse = false,
  query,
}: GetProductsParams = {}): Promise<ProductsResponse> {
  const data = await shopifyFetch<{
    products: {
      edges: Array<{ node: Product }>;
      pageInfo: PageInfo;
    };
  }>({
    query: queries.GET_PRODUCTS,
    variables: { first, after, sortKey, reverse, query },
    cache: 'no-store',
  });

  return {
    products: data.products.edges.map((edge) => edge.node),
    pageInfo: data.products.pageInfo,
  };
}

export async function getProductByHandle(handle: string): Promise<Product | null> {
  const data = await shopifyFetch<{ product: Product | null }>({
    query: queries.GET_PRODUCT_BY_HANDLE,
    variables: { handle },
    cache: 'no-store',
  });

  return data.product;
}

export async function getProductRecommendations(productId: string): Promise<Product[]> {
  const data = await shopifyFetch<{ productRecommendations: Product[] }>({
    query: queries.GET_PRODUCT_RECOMMENDATIONS,
    variables: { productId },
  });

  return data.productRecommendations || [];
}

// ============================================
// Collections
// ============================================

export async function getCollections(first = 20): Promise<Collection[]> {
  const data = await shopifyFetch<{
    collections: {
      edges: Array<{ node: Collection }>;
    };
  }>({
    query: queries.GET_COLLECTIONS,
    variables: { first },
  });

  return data.collections.edges.map((edge) => edge.node);
}

export interface CollectionWithProductCount {
  id: string;
  handle: string;
  title: string;
  description: string;
  image: { url: string; altText: string | null; width: number; height: number } | null;
  hasProducts: boolean;
}

export async function getCollectionsWithProductCount(first = 50): Promise<CollectionWithProductCount[]> {
  const data = await shopifyFetch<{
    collections: {
      edges: Array<{
        node: {
          id: string;
          handle: string;
          title: string;
          description: string;
          image: { url: string; altText: string | null; width: number; height: number } | null;
          products: {
            edges: Array<{ node: { id: string } }>;
          };
        };
      }>;
    };
  }>({
    query: queries.GET_COLLECTIONS_WITH_PRODUCT_COUNT,
    variables: { first },
  });

  return data.collections.edges.map((edge) => ({
    id: edge.node.id,
    handle: edge.node.handle,
    title: edge.node.title,
    description: edge.node.description,
    image: edge.node.image,
    hasProducts: edge.node.products.edges.length > 0,
  }));
}

interface GetCollectionParams {
  handle: string;
  first?: number;
  after?: string;
  sortKey?: string;
  reverse?: boolean;
  filters?: ProductFilter[];
}

interface CollectionResponse {
  collection: Collection | null;
  products: Product[];
  pageInfo: PageInfo;
  availableFilters: Array<{
    id: string;
    label: string;
    type: string;
    values: Array<{
      id: string;
      label: string;
      count: number;
      input: string;
    }>;
  }>;
}

export async function getCollectionByHandle({
  handle,
  first = 24,
  after,
  sortKey = 'BEST_SELLING',
  reverse = false,
  filters,
}: GetCollectionParams): Promise<CollectionResponse> {
  const data = await shopifyFetch<{
    collection: Collection & {
      products: {
        filters: CollectionResponse['availableFilters'];
        edges: Array<{ node: Product }>;
        pageInfo: PageInfo;
      };
    };
  }>({
    query: queries.GET_COLLECTION_BY_HANDLE,
    variables: { handle, first, after, sortKey, reverse, filters },
    cache: 'no-store',
  });

  if (!data.collection) {
    return {
      collection: null,
      products: [],
      pageInfo: { hasNextPage: false, hasPreviousPage: false, startCursor: null, endCursor: null },
      availableFilters: [],
    };
  }

  return {
    collection: data.collection,
    products: data.collection.products.edges.map((edge) => edge.node),
    pageInfo: data.collection.products.pageInfo,
    availableFilters: data.collection.products.filters,
  };
}

// ============================================
// Search
// ============================================

interface SearchParams {
  query: string;
  first?: number;
  after?: string;
}

interface SearchResponse {
  products: Product[];
  pageInfo: PageInfo;
  totalCount: number;
}

export async function searchProducts({
  query,
  first = 24,
  after,
}: SearchParams): Promise<SearchResponse> {
  const data = await shopifyFetch<{
    search: {
      edges: Array<{ node: Product }>;
      pageInfo: PageInfo;
      totalCount: number;
    };
  }>({
    query: queries.SEARCH_PRODUCTS,
    variables: { query, first, after },
    cache: 'no-store',
  });

  return {
    products: data.search.edges.map((edge) => edge.node),
    pageInfo: data.search.pageInfo,
    totalCount: data.search.totalCount,
  };
}

interface PredictiveSearchResult {
  products: Array<{
    id: string;
    handle: string;
    title: string;
    vendor: string;
    featuredImage: { url: string; altText: string | null } | null;
    priceRange: { minVariantPrice: { amount: string; currencyCode: string } };
  }>;
  collections: Array<{
    id: string;
    handle: string;
    title: string;
    image: { url: string; altText: string | null } | null;
  }>;
}

export async function predictiveSearch(query: string, limit = 5): Promise<PredictiveSearchResult> {
  const data = await shopifyFetch<{ predictiveSearch: PredictiveSearchResult | null }>({
    query: queries.PREDICTIVE_SEARCH,
    variables: { query, limit },
    cache: 'no-store',
  });

  return data.predictiveSearch || { products: [], collections: [] };
}

// ============================================
// Cart
// ============================================

export async function createCart(
  lines: Array<{ merchandiseId: string; quantity: number }> = []
): Promise<Cart> {
  const data = await shopifyFetch<{
    cartCreate: {
      cart: Cart;
      userErrors: Array<{ field: string; message: string }>;
    };
  }>({
    query: queries.CREATE_CART,
    variables: { input: { lines } },
    cache: 'no-store',
  });

  if (data.cartCreate.userErrors.length > 0) {
    throw new Error(data.cartCreate.userErrors.map((e) => e.message).join(', '));
  }

  setCartId(data.cartCreate.cart.id);
  return data.cartCreate.cart;
}

export async function getCart(): Promise<Cart | null> {
  const cartId = getCartId();
  if (!cartId) return null;

  try {
    const data = await shopifyFetch<{ cart: Cart | null }>({
      query: queries.GET_CART,
      variables: { cartId },
      cache: 'no-store',
    });

    if (!data.cart) {
      removeCartId();
      return null;
    }

    return data.cart;
  } catch {
    removeCartId();
    return null;
  }
}

export async function addToCart(
  merchandiseId: string,
  quantity = 1
): Promise<Cart> {
  let cartId = getCartId();

  // Create cart if it doesn't exist
  if (!cartId) {
    const cart = await createCart([{ merchandiseId, quantity }]);
    return cart;
  }

  const data = await shopifyFetch<{
    cartLinesAdd: {
      cart: Cart;
      userErrors: Array<{ field: string; message: string }>;
    };
  }>({
    query: queries.ADD_TO_CART,
    variables: {
      cartId,
      lines: [{ merchandiseId, quantity }],
    },
    cache: 'no-store',
  });

  if (data.cartLinesAdd.userErrors.length > 0) {
    throw new Error(data.cartLinesAdd.userErrors.map((e) => e.message).join(', '));
  }

  return data.cartLinesAdd.cart;
}

export async function updateCartLine(
  lineId: string,
  quantity: number
): Promise<Cart> {
  const cartId = getCartId();
  if (!cartId) throw new Error('No cart found');

  const data = await shopifyFetch<{
    cartLinesUpdate: {
      cart: Cart;
      userErrors: Array<{ field: string; message: string }>;
    };
  }>({
    query: queries.UPDATE_CART_LINE,
    variables: {
      cartId,
      lines: [{ id: lineId, quantity }],
    },
    cache: 'no-store',
  });

  if (data.cartLinesUpdate.userErrors.length > 0) {
    throw new Error(data.cartLinesUpdate.userErrors.map((e) => e.message).join(', '));
  }

  return data.cartLinesUpdate.cart;
}

export async function removeFromCart(lineId: string): Promise<Cart> {
  const cartId = getCartId();
  if (!cartId) throw new Error('No cart found');

  const data = await shopifyFetch<{
    cartLinesRemove: {
      cart: Cart;
      userErrors: Array<{ field: string; message: string }>;
    };
  }>({
    query: queries.REMOVE_FROM_CART,
    variables: {
      cartId,
      lineIds: [lineId],
    },
    cache: 'no-store',
  });

  if (data.cartLinesRemove.userErrors.length > 0) {
    throw new Error(data.cartLinesRemove.userErrors.map((e) => e.message).join(', '));
  }

  return data.cartLinesRemove.cart;
}

// ============================================
// Pages (CMS Content)
// ============================================

export interface Metafield {
  key: string;
  namespace: string;
  value: string;
  type: string;
}

export interface ShopifyPage {
  id: string;
  handle: string;
  title: string;
  body: string;
  bodySummary: string;
  seo: {
    title: string | null;
    description: string | null;
  };
  createdAt: string;
  updatedAt: string;
  metafields?: (Metafield | null)[];
}

export interface ShopMetafields {
  name: string;
  description: string;
  brand?: {
    shortDescription: string | null;
    slogan: string | null;
  };
  metafields?: (Metafield | null)[];
}

export async function getPageByHandle(handle: string): Promise<ShopifyPage | null> {
  try {
    const data = await shopifyFetch<{ page: ShopifyPage | null }>({
      query: queries.GET_PAGE_BY_HANDLE,
      variables: { handle },
    });
    return data.page;
  } catch {
    return null;
  }
}

export async function getPages(first = 50): Promise<Array<{ id: string; handle: string; title: string }>> {
  try {
    const data = await shopifyFetch<{
      pages: {
        edges: Array<{ node: { id: string; handle: string; title: string } }>;
      };
    }>({
      query: queries.GET_PAGES,
      variables: { first },
    });
    return data.pages.edges.map((edge) => edge.node);
  } catch {
    return [];
  }
}

export async function getShopMetafields(): Promise<ShopMetafields | null> {
  try {
    const data = await shopifyFetch<{ shop: ShopMetafields }>({
      query: queries.GET_SHOP_METAFIELDS,
    });
    return data.shop;
  } catch {
    return null;
  }
}

// ===========================================
// Metafield Utilities
// ===========================================

/**
 * Parse metafields array into a key-value object
 */
export function parseMetafields(metafields?: (Metafield | null)[]): Record<string, unknown> {
  if (!metafields) return {};

  return metafields
    .filter((m): m is Metafield => m !== null)
    .reduce((acc, field) => {
      let value: unknown = field.value;

      // Parse JSON types
      if (field.type === 'json' || field.type === 'list.single_line_text_field') {
        try {
          value = JSON.parse(field.value);
        } catch {
          value = field.value;
        }
      }

      // Parse numbers
      if (field.type === 'number_integer' || field.type === 'number_decimal') {
        value = parseFloat(field.value);
      }

      // Parse booleans
      if (field.type === 'boolean') {
        value = field.value === 'true';
      }

      acc[field.key] = value;
      return acc;
    }, {} as Record<string, unknown>);
}

/**
 * Get a specific metafield value with type safety
 */
export function getMetafieldValue<T>(
  metafields: Record<string, unknown>,
  key: string,
  defaultValue: T
): T {
  const value = metafields[key];
  if (value === undefined || value === null) return defaultValue;
  return value as T;
}

// ============================================
// Blog
// ============================================

export interface BlogArticle {
  id: string;
  handle: string;
  title: string;
  excerpt: string | null;
  contentHtml: string;
  publishedAt: string;
  authorV2: {
    name: string;
  } | null;
  image: {
    url: string;
    altText: string | null;
    width: number;
    height: number;
  } | null;
  tags: string[];
  seo?: {
    title: string | null;
    description: string | null;
  };
}

export async function getBlogArticles(blogHandle = 'news', first = 20): Promise<BlogArticle[]> {
  try {
    const data = await shopifyFetch<{
      blog: {
        id: string;
        title: string;
        articles: {
          edges: Array<{ node: BlogArticle }>;
        };
      } | null;
    }>({
      query: queries.GET_BLOG_ARTICLES,
      variables: { blogHandle, first },
    });

    if (!data.blog) return [];
    return data.blog.articles.edges.map((edge) => edge.node);
  } catch {
    return [];
  }
}

export async function getBlogArticle(articleHandle: string, blogHandle = 'news'): Promise<BlogArticle | null> {
  try {
    const data = await shopifyFetch<{
      blog: {
        articleByHandle: BlogArticle | null;
      } | null;
    }>({
      query: queries.GET_BLOG_ARTICLE,
      variables: { blogHandle, articleHandle },
    });

    return data.blog?.articleByHandle || null;
  } catch {
    return null;
  }
}

// ============================================
// Utility Functions
// ============================================

export function formatPrice(amount: string, currencyCode: string): string {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: currencyCode,
  }).format(parseFloat(amount));
}

export function getProductUrl(handle: string): string {
  return `/products/${handle}`;
}

// ============================================
// Newsletter / Customer Marketing
// ============================================

export async function subscribeToNewsletter(email: string): Promise<{ success: boolean; error?: string }> {
  try {
    const data = await shopifyFetch<{
      customerCreate: {
        customer: { id: string; email: string; acceptsMarketing: boolean } | null;
        customerUserErrors: Array<{ code: string; field: string[]; message: string }>;
      };
    }>({
      query: queries.CUSTOMER_CREATE,
      variables: {
        input: {
          email,
          acceptsMarketing: true,
        },
      },
      cache: 'no-store',
    });

    const errors = data.customerCreate.customerUserErrors;
    if (errors.length > 0) {
      // TAKEN means already subscribed — treat as success
      if (errors.some((e) => e.code === 'TAKEN')) {
        return { success: true };
      }
      return { success: false, error: errors[0].message };
    }

    return { success: true };
  } catch (err) {
    return { success: false, error: 'Something went wrong. Please try again.' };
  }
}

export function getCollectionUrl(handle: string): string {
  return `/collections/${handle}`;
}

export function calculateDiscount(price: string, compareAtPrice: string): number {
  const current = parseFloat(price);
  const original = parseFloat(compareAtPrice);
  if (original <= current) return 0;
  return Math.round(((original - current) / original) * 100);
}
